/**
 * Approval Workflow Integration Component
 *
 * FERP CRITICAL: Submit refined templates to Template Approval Orchestrator
 * Handles criticality-based routing, dual-control tracking, and decision audit trail
 *
 * FERP Principles:
 * - Data-driven (approval policies from ApprovalPolicyRepository)
 * - Repository pattern (NO direct GraphQL calls)
 * - Backend ID generation (useGenerateId for approval IDs)
 * - Life-critical (dual-control for high-risk templates)
 * - Immutable audit trail (all decisions cryptographically signed)
 *
 * Approval Flow:
 * 1. Determine Criticality (HIPAA/FDA = life-critical, SOX = critical, none = operational)
 * 2. Load Approval Policy (from DynamoDB per tenant/domain/criticality)
 * 3. Assign Approvers (default + expert if confidence < 0.85 + compliance if violations)
 * 4. Create ApprovalWorkflowInstance (PendingReview)
 * 5. Track Decisions (Approve/Reject/RevisionRequested)
 * 6. Publish Template (if Approved)
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Stack,
  Card,
  Group,
  Text,
  Badge,
  Button,
  Alert,
  Stepper,
  Timeline,
  Paper,
  Table,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Progress,
  RingProgress,
  Center,
  Textarea,
  Select,
  Divider,
  Grid,
} from '@mantine/core';
import {
  IconCheck,
  IconAlertCircle,
  IconClock,
  IconUserCheck,
  IconUsers,
  IconShieldCheck,
  IconSend,
  IconRefresh,
  IconFileCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useCurrentTenant } from '../hooks/useCurrentTenant';
import { useAuth } from '../security/useAuth';
import { ApprovalRepository, ApprovalStatus, DecisionType } from '../data/repositories';
import type { ApprovalWorkflowInstance } from '../data/repositories/ApprovalRepository';

/**
 * Template metadata for submission
 */
export interface TemplateForApproval {
  template_id: string;
  template_version: string;
  tenant: string;
  business: {
    name: string;
    domain: string;
    category: string;
  };
  compliance: {
    frameworks: string[];
  };
  confidence_score: number;
}

/**
 * Criticality level
 */
export type CriticalityLevel = 'life_critical' | 'critical' | 'operational';

/**
 * Approval decision
 */
export interface ApprovalDecision {
  decision_id: string;
  approver_id: string;
  approver_name: string;
  approver_role: string;
  decision: 'Approved' | 'Rejected' | 'RevisionRequested';
  comments: string;
  timestamp: string;
}

export interface ApprovalWorkflowIntegrationProps {
  template: TemplateForApproval;
  onSubmitSuccess?: (instanceId: string) => void;
  onSubmitError?: (error: string) => void;
}

/**
 * Approval Workflow Integration Component
 *
 * FERP Pattern: Criticality-based approval routing with dual-control
 */
export function ApprovalWorkflowIntegration({
  template,
  onSubmitSuccess,
  onSubmitError,
}: ApprovalWorkflowIntegrationProps): JSX.Element {
  const { tenantId } = useCurrentTenant();
  const { user } = useAuth();

  // Repository pattern - instantiate ApprovalRepository
  const approvalRepository = new ApprovalRepository();

  const [activeStep, setActiveStep] = useState(0);
  const [criticality, setCriticality] = useState<CriticalityLevel>('operational');
  const [approvalInstance, setApprovalInstance] = useState<ApprovalWorkflowInstance | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitterComments, setSubmitterComments] = useState('');

  /**
   * Determine template criticality based on compliance frameworks
   */
  const determineCriticality = useCallback((): CriticalityLevel => {
    const frameworks = template.compliance.frameworks;

    // Life-critical: HIPAA, FDA, patient safety
    if (
      frameworks.some(f =>
        ['HIPAA', 'FDA', 'ISO_13485', 'IEC_62304'].includes(f)
      )
    ) {
      return 'life_critical';
    }

    // Critical: SOX, PCI, financial compliance
    if (
      frameworks.some(f => ['SOX', 'PCI_DSS', 'FFIEC', 'FINRA'].includes(f))
    ) {
      return 'critical';
    }

    // Operational: General business workflows
    return 'operational';
  }, [template.compliance.frameworks]);

  /**
   * Load approval policy for template
   * Note: This will trigger submission directly since policy is determined on backend
   */
  const loadApprovalPolicy = useCallback(async () => {
    if (!tenantId) return;

    try {
      // Backend determines policy automatically, so we skip to submission
      setActiveStep(1);
    } catch (error) {
      console.error('Failed to load approval policy:', error);
      onSubmitError?.('Failed to load approval policy');
    }
  }, [tenantId, onSubmitError]);

  /**
   * Submit template for approval
   */
  const handleSubmitForApproval = useCallback(async () => {
    if (!tenantId || !user) return;

    setIsSubmitting(true);

    try {
      const instance = await approvalRepository.submitForApproval(
        template.template_id,
        template.template_version,
        user.id
      );

      setApprovalInstance(instance);
      setActiveStep(2);

      notifications.show({
        title: 'Template Submitted for Approval',
        message: `Approval workflow instance ${instance.instanceId} created`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      onSubmitSuccess?.(instance.instanceId);
    } catch (error) {
      console.error('Failed to submit for approval:', error);
      notifications.show({
        title: 'Submission Failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
      onSubmitError?.(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    tenantId,
    user,
    template,
    submitterComments,
    criticality,
    onSubmitSuccess,
    onSubmitError,
    approvalRepository,
  ]);

  /**
   * Initialize criticality determination
   */
  useEffect(() => {
    const determined = determineCriticality();
    setCriticality(determined);
  }, [determineCriticality]);

  /**
   * Get criticality color
   */
  const getCriticalityColor = (level: CriticalityLevel): string => {
    switch (level) {
      case 'life_critical':
        return 'red';
      case 'critical':
        return 'orange';
      case 'operational':
        return 'blue';
    }
  };

  /**
   * Get criticality label
   */
  const getCriticalityLabel = (level: CriticalityLevel): string => {
    switch (level) {
      case 'life_critical':
        return 'Life-Critical';
      case 'critical':
        return 'Critical';
      case 'operational':
        return 'Operational';
    }
  };

  /**
   * Get deadline display
   */
  const getDeadlineDisplay = (criticality: CriticalityLevel): string => {
    switch (criticality) {
      case 'life_critical':
        return '48 hours';
      case 'critical':
        return '72 hours';
      case 'operational':
        return '168 hours (7 days)';
    }
  };

  /**
   * Render criticality determination step
   */
  const renderCriticalityStep = (): JSX.Element => (
    <Stack gap="md">
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon color={getCriticalityColor(criticality)}>
              <IconShieldCheck size={20} />
            </ThemeIcon>
            <Text size="lg" fw={700}>
              Criticality: {getCriticalityLabel(criticality)}
            </Text>
          </Group>
          <Badge color={getCriticalityColor(criticality)} size="lg">
            {criticality.toUpperCase()}
          </Badge>
        </Group>

        <Text size="sm" color="dimmed" mb="md">
          Criticality determined by compliance frameworks and domain
        </Text>

        <Table>
          <tbody>
            <tr>
              <td>
                <Text size="sm" fw={500}>
                  Compliance Frameworks
                </Text>
              </td>
              <td>
                <Group gap="xs">
                  {template.compliance.frameworks.map((fw, idx) => (
                    <Badge key={idx} variant="outline">
                      {fw}
                    </Badge>
                  ))}
                </Group>
              </td>
            </tr>
            <tr>
              <td>
                <Text size="sm" fw={500}>
                  Domain
                </Text>
              </td>
              <td>
                <Badge>{template.business.domain}</Badge>
              </td>
            </tr>
            <tr>
              <td>
                <Text size="sm" fw={500}>
                  Confidence Score
                </Text>
              </td>
              <td>
                <Badge
                  color={template.confidence_score >= 0.85 ? 'green' : 'orange'}
                >
                  {(template.confidence_score * 100).toFixed(0)}%
                </Badge>
              </td>
            </tr>
            <tr>
              <td>
                <Text size="sm" fw={500}>
                  Approval Deadline
                </Text>
              </td>
              <td>
                <Group gap="xs">
                  <IconClock size={16} />
                  <Text size="sm">{getDeadlineDisplay(criticality)}</Text>
                </Group>
              </td>
            </tr>
          </tbody>
        </Table>
      </Card>

      {criticality === 'life_critical' && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          <Text fw={500}>Life-Critical Template - Dual Control Required</Text>
          <Text size="sm">
            This template requires 2+ independent approvals due to patient safety implications.
            All decisions will be cryptographically signed and immutably audited.
          </Text>
        </Alert>
      )}

      {template.confidence_score < 0.85 && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          <Text fw={500}>Low Confidence - Expert Review Required</Text>
          <Text size="sm">
            Confidence score below 85% threshold triggers additional expert review.
            An expert approver will be automatically assigned.
          </Text>
        </Alert>
      )}

      <Button
        leftSection={<IconRefresh size={16} />}
        onClick={loadApprovalPolicy}
        fullWidth
      >
        Load Approval Policy
      </Button>
    </Stack>
  );

  /**
   * Render approval policy step
   */
  const renderApprovalPolicyStep = (): JSX.Element => {
    return (
      <Stack gap="md">
        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          <Text fw={500}>Approval Policy</Text>
          <Text size="sm">
            The backend will automatically determine the appropriate approval policy based on
            template criticality, domain, and compliance frameworks.
          </Text>
        </Alert>

        <Textarea
          label="Submitter Comments (Optional)"
          description="Provide context or notes for approvers"
          placeholder="Enter any additional information for approvers..."
          value={submitterComments}
          onChange={e => setSubmitterComments(e.currentTarget.value)}
          minRows={3}
        />

        <Button
          leftSection={<IconSend size={16} />}
          onClick={handleSubmitForApproval}
          loading={isSubmitting}
          fullWidth
          color="green"
        >
          Submit for Approval
        </Button>
      </Stack>
    );
  };

  /**
   * Render approval tracking step
   */
  const renderApprovalTrackingStep = (): JSX.Element => {
    if (!approvalInstance) return <div>No approval instance</div>;

    const deadline = new Date(approvalInstance.deadline);
    const now = new Date();
    const timeRemaining = Math.max(0, deadline.getTime() - now.getTime());
    const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
    const approvedCount = approvalInstance.decisions.filter(d => d.decisionType === 'APPROVED').length;
    const requiredCount = approvalInstance.requiredApprovers.length;

    return (
      <Stack gap="md">
        <Alert icon={<IconCheck size={16} />} color="green">
          <Text fw={500}>Template Submitted Successfully</Text>
          <Text size="sm">
            Approval workflow instance {approvalInstance.instanceId} created. Approvers have been
            notified.
          </Text>
        </Alert>

        <Card withBorder>
          <Group justify="space-between" mb="md">
            <Text size="lg" fw={700}>
              Approval Progress
            </Text>
            <Badge color={approvalInstance.status === ApprovalStatus.PENDING_REVIEW ? 'blue' : 'green'}>
              {approvalInstance.status}
            </Badge>
          </Group>

          <Grid gutter="md" mb="md">
            <Grid.Col span={6}>
              <Center>
                <RingProgress
                  size={120}
                  thickness={12}
                  sections={[
                    {
                      value: (approvedCount / requiredCount) * 100,
                      color: approvedCount >= requiredCount ? 'green' : 'blue',
                    },
                  ]}
                  label={
                    <Center>
                      <Stack gap={0} ta="center">
                        <Text size="lg" fw={700}>
                          {approvedCount}/{requiredCount}
                        </Text>
                        <Text size="xs" color="dimmed">
                          Approvals
                        </Text>
                      </Stack>
                    </Center>
                  }
                />
              </Center>
            </Grid.Col>

            <Grid.Col span={6}>
              <Stack gap="xs">
                <Paper p="xs" withBorder>
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      Time Remaining
                    </Text>
                    <Badge color={hoursRemaining < 12 ? 'red' : 'blue'}>
                      {hoursRemaining}h
                    </Badge>
                  </Group>
                </Paper>
                <Paper p="xs" withBorder>
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      Required Approvers
                    </Text>
                    <Badge>{requiredCount}</Badge>
                  </Group>
                </Paper>
                <Paper p="xs" withBorder>
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>
                      Approved
                    </Text>
                    <Badge color="green">{approvedCount}</Badge>
                  </Group>
                </Paper>
              </Stack>
            </Grid.Col>
          </Grid>

          {approvalInstance.decisions.length > 0 && (
            <>
              <Divider my="md" />
              <Text size="sm" fw={500} mb="xs">
                Decision History
              </Text>
              <Timeline>
                {approvalInstance.decisions.map((decision, idx) => (
                  <Timeline.Item
                    key={idx}
                    bullet={
                      decision.decisionType === DecisionType.APPROVED ? (
                        <IconCheck size={12} />
                      ) : (
                        <IconAlertCircle size={12} />
                      )
                    }
                    color={decision.decisionType === DecisionType.APPROVED ? 'green' : 'red'}
                    title={
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {decision.approverName}
                        </Text>
                        <Badge
                          size="sm"
                          color={decision.decisionType === DecisionType.APPROVED ? 'green' : 'red'}
                        >
                          {decision.decisionType}
                        </Badge>
                      </Group>
                    }
                  >
                    <Text size="xs" color="dimmed">
                      {decision.approverRole} • {decision.decidedAt}
                    </Text>
                    {decision.comments && (
                      <Text size="xs" mt={4}>
                        {decision.comments}
                      </Text>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            </>
          )}
        </Card>
      </Stack>
    );
  };

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon color="blue">
              <IconFileCheck size={20} />
            </ThemeIcon>
            <Text size="lg" fw={700}>
              Template Approval Workflow
            </Text>
          </Group>
          <Tooltip label="Criticality-based approval routing with dual-control for life-critical templates">
            <ActionIcon size="sm" variant="subtle">
              <IconInfoCircle size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Text size="sm" color="dimmed" mb="md">
          Template: {template.business.name} (v{template.template_version})
        </Text>

        <Stepper active={activeStep}>
          <Stepper.Step
            label="Criticality"
            description="Determine approval requirements"
            icon={<IconShieldCheck size={18} />}
          >
            {renderCriticalityStep()}
          </Stepper.Step>

          <Stepper.Step
            label="Policy"
            description="Load approval policy"
            icon={<IconUsers size={18} />}
          >
            {renderApprovalPolicyStep()}
          </Stepper.Step>

          <Stepper.Step
            label="Tracking"
            description="Monitor approval progress"
            icon={<IconUserCheck size={18} />}
          >
            {renderApprovalTrackingStep()}
          </Stepper.Step>
        </Stepper>
      </Card>
    </Stack>
  );
}
